import {Col, Pagination, Row, Space, Table, Typography} from 'antd';
import _, {capitalize} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import DeleteMultipleDocuments from '../DeleteMultipleDocuments';
import {DownloadMultipleDocuments} from '../DownloadMultipleDocuments';
import {DocumentType, useDeleteDocumentMutation, useLazyGetDocumentsByEventsQuery} from '../../../redux/api/documents';
import {dateToLocaleString} from '../../../utils/dateParsers';
import {useGetUserProfileQuery} from '../../../redux/api/locales';
import {DocumentActions} from '../../common/documents/documentActions/DocumentActions';
import {CustomLoading} from '../../common/CustomLoading';
import {useDispatch, useSelector} from 'react-redux';
import {fromFeToBeSorter} from '../../../utils/parser';
import {
  selectEventsDocuments,
  setEventsDocumentsFilters,
  setEventsDocumentsSorters,
  setEventsDocumentsTablePagination,
} from '../../../redux/slices/documents';
import {DocumentsTableColumnHeader} from './DocumentsTableColumnHeader';
import TableSorter from '../../common/TableSorter';
import {DocumentsFileNameFilter} from './filters/DocumentsFileNameFilter';
import {DocumentsEndDateFilter} from './filters/DocumentsEndDateFilter';
import {isEventDocumentDelete, isEventDocumentEdit, isEventDocumentView} from '../../Authentication/permissions';
import {VALUE_NOT_PRESENT} from "../../../utils/translation";

const {Text} = Typography;
export default function EventsDocumentsTableView() {

  const {t} = useTranslation();
  const dispatch = useDispatch();

  const [
    selectedRowKeys,
    setSelectedRowKeys,
  ] = useState<React.Key[]>([]);

  const pagination = useSelector(selectEventsDocuments).pagination;
  const filters = useSelector(selectEventsDocuments).filters;
  const sorters = useSelector(selectEventsDocuments).sorters;

  const [
    getDocuments,
    {
      data: documents,
      isLoading: isLoadingDocuments,
      isFetching: isFetchingDocuments,
    },
  ] = useLazyGetDocumentsByEventsQuery();
  const {
    data: userData,
    isFetching: isFetchingUserData,
    isLoading: isLoadingUserData,
  } = useGetUserProfileQuery();

  const isEditing = userData?.type ? isEventDocumentEdit(userData?.type) : false;
  const isRemove = userData?.type ? isEventDocumentDelete(userData?.type) : false;
  const isDownload = userData?.type ? isEventDocumentView(userData?.type) : false;

  const [
    deleteDocument,
  ] = useDeleteDocumentMutation();

  useEffect(() => {
    getDocuments({
      page: pagination.current,
      page_size: pagination.pageSize,
      ...(filters.name ? {
        file_name: filters.name?.map(name => name.value).join(','),
      } : {}),
      ...(filters.end_date ? {
        end_date_before: filters.end_date.at(1),
        end_date_after: filters.end_date.at(0),
      } : {}),
      ...(!_.isEmpty(fromFeToBeSorter(sorters)) ? {
        ordering: fromFeToBeSorter(sorters),
      } : {}),
    });
  }, [filters.end_date, filters.name, getDocuments, pagination, sorters]);

  const columns = useMemo(() => [
    {
      dataIndex: 'name',
      key: 'name',
      width: 300,
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t('documents.fileName'))}
          sorter={<TableSorter
            value={sorters.name}
            onChange={type => dispatch(setEventsDocumentsSorters({name: type}))}
          />}
          filter={<DocumentsFileNameFilter
            activeFilters={filters.name}
            handleFilterChange={(values) => dispatch(setEventsDocumentsFilters({name: values}))}
            viewType={'events'}
          />}
          onFilterReset={() => dispatch(setEventsDocumentsFilters({name: null}))}
          isFilterDisabled={_.isEmpty(filters.name)}
        />
      ),
    },
    {
      //title: capitalize(t('documents.validityDate')),
      dataIndex: 'end_date',
      key: 'end_date',
      width: 300,
      render: (text: string, record: DocumentType, index: number) => {
        if (userData)
          return record.end_date ? dateToLocaleString(record.end_date, userData.language) :
            <Text type={'secondary'}>{VALUE_NOT_PRESENT}</Text>;
      },
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t('documents.validityDate'))}
          sorter={<TableSorter
            value={sorters.end_date}
            onChange={type => dispatch(setEventsDocumentsSorters({end_date: type}))}
          />}
          filter={<DocumentsEndDateFilter
            activeFilters={filters.end_date}
            handleFilterChange={(values) => dispatch(setEventsDocumentsFilters({end_date: [values[0].format('YYYY-MM-DD'), values[1].format('YYYY-MM-DD')]}))}
          />}
          onFilterReset={() => dispatch(setEventsDocumentsFilters({end_date: null}))}
          isFilterDisabled={_.isEmpty(filters.end_date)}
        />
      ),
    },
    {
      title: capitalize(t('emergency.emergency')),
      width: 300,
    },
    {
      key: 'action',
      width: 300,
      render: (text: string, record: DocumentType, index: number) => {
        return <DocumentActions
          documentItem={record}
          withDetails={true}
          withDownload={isDownload}
          withEdit={isEditing}
          withRemove={isRemove}
          onRemove={() => deleteDocument({documentUuid: record.uuid}).unwrap()}
        />;
      },
    }], [deleteDocument, dispatch, filters.end_date, filters.name, isDownload, isEditing, isRemove, sorters.end_date, sorters.name, t, userData]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_NONE,
    ],
  };

  useEffect(() => {
    // todo onChange pagination reset selection
  }, []);

  return <>
    {!_.isEmpty(selectedRowKeys) && <Col>
      <Space>
        {isRemove && <DeleteMultipleDocuments isDeleteForbidden={documents?.results.some(el => el.protected && selectedRowKeys.includes(el.uuid))} uuids={selectedRowKeys as string[]}/>}
        {isDownload && <DownloadMultipleDocuments uuids={selectedRowKeys as string[]}/>}
      </Space>
    </Col>}
    <Col xs={24}>
      <Table
        columns={columns}
        pagination={false}
        rowSelection={rowSelection}
        loading={
          {
            spinning: isLoadingDocuments || isFetchingDocuments || isLoadingUserData || isFetchingUserData,
            indicator: <CustomLoading/>,
          }
        }
        dataSource={documents?.results.map(document => ({key: document.uuid, ...document}))}
        scroll={{x: true}}
      />
      <Row
        justify={'center'}
        style={{marginTop: '1rem'}}
      >
        <Col>
          <Pagination
            disabled={!documents?.count}
            showSizeChanger={true}
            total={documents?.count}
            showTotal={(total, range) => t('table.pageSizeOfTotal', {
              rangeStart: range[0],
              rangeEnd: range[1],
              total,
            })}
            onChange={(current, pageSize) => {
              dispatch(setEventsDocumentsTablePagination({current, pageSize}));
            }}
            {...pagination}
          />
        </Col>
      </Row>
    </Col>
  </>;
}